import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { Axle, CabinDimensions, LoadingSpaceType, ProjectOptions } from 'src/app/core/types';

import { FADE_IN_OUT_ANIMATION } from 'src/app/shared/animations/animations';
import { Subscription } from 'rxjs';
import { CabinDimensionsGenerator } from '../../../../core/utils/cabin-dimensions-generator';
import { ProjectService } from '../../../../core/services/project.service';
import { pluck } from 'rxjs/operators';
import { validateMin } from '../../../../core/validators/validators';
import { AxleValue } from '../../../../shared/components/axle-loads/axle-loads.component';

interface FormValues {
  distanceFromSpaceFront?: number;
  emptySpaceLoad?: number;
  maxLoad?: number;
  isBus?: boolean;
  quantity?: number;
  selectedAxle?: number;
}

@Component({
    selector: 'gl-loading-space-axle-loads',
    templateUrl: './loading-space-axle-loads.component.html',
    styleUrls: ['./loading-space-axle-loads.component.scss'],
    animations: [FADE_IN_OUT_ANIMATION],
    standalone: false
})
export class LoadingSpaceAxleLoadsComponent implements OnInit, OnChanges, OnDestroy {
  @Output() public submitEmitter = new EventEmitter<Axle[]>();
  @Output() public scrollDisable = new EventEmitter();
  @Input() public setActive: boolean;
  @Input() public loadForm: UntypedFormGroup;
  @Input() public loadingSpace: LoadingSpaceType;
  @Input() public chosenLoadingSpace: LoadingSpaceType;
  @Input() public isEdit = false;
  @ViewChild('f', { static: false }) public formEl: FormGroupDirective;
  @ViewChild('infoTrigger', { static: false }) public infoTriggerEl: ElementRef<HTMLElement>;
  @ViewChild('infoWindow', { static: false }) public infoWindowEl: ElementRef<HTMLElement>;
  @ViewChild('infoArrow', { static: false }) public infoArrowEl: ElementRef<HTMLElement>;
  public form: CustomForm;
  public isInfoWindowOpen = false;
  public isSwitchSet = false;
  public axlesNrList: number[] = [1, 2];
  public axles: Axle[] = [];
  public semiAxles: Axle[] = [];
  public trailerAxles: Axle[] = [];
  private subscriptions: Subscription[] = [];
  private cabinLength = 0;
  private busRange = { min: 2, max: 3 };
  private semiTrailerRange = { min: 3, max: 5 };

  //  private defaultAxles: Axle[] = [];

  constructor(private renderer: Renderer2, private projectService: ProjectService) {}

  ngOnDestroy(): void {
    this.emitAxles();
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  public currentAxleValue(value: AxleValue): void {
    this.form.controls.distanceFromSpaceFront.patchValue(value.axleValue, { emitEvent: false });
    this.changeAxleValue(value);
  }

  private changeAxleValue(value: AxleValue): void {
    if (this.axles[value.index]) {
      this.axles[value.index].distanceFromSpaceFront = value.axleValue;
      this.axles = [...this.axles];
    }
    this.emitAxles();
  }

  private setAxles(): void {
    const isChosenLoadingSpace: boolean = this.chosenLoadingSpace !== undefined;
    const dataSource = this.chosenLoadingSpace;
    if (isChosenLoadingSpace && dataSource.axis !== undefined) {
      this.trailerAxles = dataSource.axis.filter(
        (axle: Axle): boolean => axle.loadingSpacePart === 2,
      );
      this.semiAxles = dataSource.axis.filter((axle: Axle): boolean => axle.loadingSpacePart === 1);
    } else {
      this.trailerAxles = [];
      this.semiAxles = [];
    }
    this.semiAxles = this.semiAxles.length > 0 ? this.semiAxles : this.generateAxles(1);
    this.trailerAxles = this.trailerAxles.length > 0 ? this.trailerAxles : this.generateAxles(2);
    const isAxis: boolean = this.semiAxles.length > 0;
    this.axles = isAxis ? this.semiAxles : this.generateAxles(1);
  }

  private generateAxles(loadingSpacePart: number): Axle[] {
    const numberOfAxles = 2;
    return Array.from({ length: numberOfAxles }, () => this.generateEmptyAxle(loadingSpacePart));
  }

  private generateEmptyAxle(loadingSpacePart: number): Axle {
    return {
      distanceFromSpaceFront: null,
      emptySpaceLoad: null,
      maxLoad: null,
      loadingSpacePart,
    };
  }

  public ngOnInit(): void {
    this.form = this.generateAxleLoadsForm();
    this.setAxles();
    if (this.isEdit) {
      this.form.enable();
    } else {
      this.form.disable();
    }

    this.onChosen(this.loadingSpace);
    this.setCabinLength();
    this.listenForFormValueChanges();
    this.listenForSelectedAxleChanges();
    this.listenForQuantityChanges();
    // this.listenForBusChanges();
    this.initFormData();
    this.setNrOfAxles(this.axles.length);
  }

  private setCabinLength(): void {
    this.projectService.project
      .pipe(pluck('options'))
      .subscribe((options: ProjectOptions): void => {
        const cabinDimensions: CabinDimensions = CabinDimensionsGenerator(
          options?.unit,
          this.loadingSpace,
        );
       this.cabinLength = this.loadingSpace.unit === 'mm' ? cabinDimensions.length * 10 : cabinDimensions.length;
      });
  }

  private validateAxlesQuantity(quantity: number): boolean {
    const isBus = true;
    const quantityControlValue: number = quantity;
    const isWithinRange: boolean = isBus
      ? quantityControlValue >= this.busRange.min && quantityControlValue <= this.busRange.max
      : quantityControlValue >= this.semiTrailerRange.min &&
        quantityControlValue <= this.semiTrailerRange.max;
    if (!isWithinRange) {
      const numberOfAxles = isBus
        ? Math.min(Math.max(quantityControlValue, this.busRange.min), this.busRange.max)
        : Math.min(
            Math.max(quantityControlValue, this.semiTrailerRange.min),
            this.semiTrailerRange.max,
          );
      this.form.controls['quantity'].patchValue(numberOfAxles, { emitEvent: false });
    }
    return isWithinRange;
  }

  private listenForFormValueChanges(): void {
    this.subscriptions.push(
      this.form.valueChanges.subscribe((formValues: FormValues): void => {
        const selectedAxle: number = formValues.selectedAxle - 1;
        if (this.axles[selectedAxle]) {
          this.axles[selectedAxle].distanceFromSpaceFront =
            formValues.distanceFromSpaceFront !== null
              ? Number(formValues.distanceFromSpaceFront)
              : null;
          this.axles[selectedAxle].emptySpaceLoad =
            formValues.emptySpaceLoad !== null ? Number(formValues.emptySpaceLoad) : null;
          this.axles[selectedAxle].maxLoad =
            formValues.maxLoad !== null ? Number(formValues.maxLoad) : null;
          const isDistanceFromSpaceFrontValid = this.form.controls.distanceFromSpaceFront.valid;
          if (isDistanceFromSpaceFrontValid) {
            this.axles = [...this.axles];
          }
          if (this.isSwitchSet) {
            this.trailerAxles = this.axles;
          } else {
            this.semiAxles = this.axles;
          }
          this.emitAxles();
        }
      }),
    );
  }

  private setNrOfAxles(nr: number): void {
    this.form.controls.quantity.patchValue(nr, { emitEvent: false });
  }

  private setSelectedAxle(nr: number): void {
    this.form.controls.selectedAxle.patchValue(nr, { emitEvent: false });
  }

  // private listenForBusChanges(): void {
  //   this.form.controls.isBus.valueChanges.subscribe((value: boolean): void => {
  //     const isBus = value;
  //     const maxQuantity: number = isBus ? this.busRange.max : this.semiTrailerRange.max;
  //     const minQuantity: number = isBus ? this.busRange.max : this.semiTrailerRange.min;
  //     const currentQuantity: number = this.form.controls.quantity.value as unknown as number;
  //     const isWithinRange: boolean = currentQuantity > minQuantity && currentQuantity < maxQuantity;
  //     const quantity: number = isWithinRange ? currentQuantity : minQuantity;
  //     this.form.controls.quantity.patchValue(quantity, { emitEvent: false });
  //     this.changeAxlesNr(quantity);
  //     this.axlesNrList = this.generateAxlesNrList();
  //   });
  // }

  private listenForQuantityChanges(): void {
    this.form.controls.quantity.valueChanges.subscribe((value: number) => {
      const isWithinRange: boolean = this.validateAxlesQuantity(value);
      if (isWithinRange) {
        this.changeAxlesNr(value);
        this.axlesNrList = this.generateAxlesNrList();
      }
    });
  }

  private listenForSelectedAxleChanges(): void {
    this.form.controls.selectedAxle.valueChanges.subscribe((value: number) => {
      this.fillFormWithAxleData(value - 1);
    });
  }

  private initFormData(): void {
    this.fillFormWithAxleData(0);
  }

  private fillFormWithAxleData(index: number): void {
    if (this.axles.length === 0) {
      return;
    }
    this.form.patchValue(
      {
        distanceFromSpaceFront:
          this.axles[index].distanceFromSpaceFront !== undefined
            ? this.axles[index].distanceFromSpaceFront
            : this.axles[index].position.z,
        emptySpaceLoad: this.axles[index].emptySpaceLoad,
        maxLoad: this.axles[index].maxLoad,
      },
      { emitEvent: false },
    );
  }

  private generateAxlesNrList(): number[] {
    const axlesNrList: number[] = [];
    this.axles.forEach((_, index: number): void => {
      axlesNrList.push(index + 1);
    });
    return axlesNrList;
  }

  private changeAxlesNr(value: number): void {
    const axlesLength: number = this.axles.length;
    if (value > axlesLength) {
      const axlesToAdd: number = value - axlesLength;
      for (let i = 0; i < axlesToAdd; i++) {
        this.addAxle();
      }
    } else if (value < axlesLength) {
      const axlesToRemove: number = axlesLength - value;
      for (let i = 0; i < axlesToRemove; i++) {
        this.removeAxle();
      }
    }
    this.emitAxles();
  }

  private addAxle(): void {
    const axleLoadingPart = this.isSwitchSet ? 2 : 1;
    const newAxle: Axle = this.generateEmptyAxle(axleLoadingPart);
    this.axles.push(newAxle);
    this.axles = [...this.axles];
  }

  private removeAxle(): void {
    this.axles.pop();
    this.axles = [...this.axles];
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isEdit?.currentValue && this.form !== undefined) {
      this.isEdit = true;
      this.form.enable();
    } else if (changes.isEdit && !changes.isEdit.currentValue && this.form !== undefined) {
      this.isEdit = false;
      this.form.disable();
    }
  }

  public onChosen(loadingSpace: LoadingSpaceType): void {
    this.switchSet(false);
    this.setActive = loadingSpace.set;
    return;
  }

  public switchSet(set: boolean): void {
    this.isSwitchSet = set;
    if (set) {
      this.cabinLength = 0;
    } else {
      this.setCabinLength();
    }
    this.switchAxles();
    this.fillFormWithAxleData(0);
    this.setSelectedAxle(1);
    this.setNrOfAxles(this.axles.length);
  }

  private switchAxles(): void {
    this.axles = this.isSwitchSet ? this.trailerAxles : this.semiAxles;
    this.axlesNrList = this.generateAxlesNrList();
  }

  private validateAxles(axles: Axle[]): boolean {
    return axles.every((axle: Axle): boolean => {
      const isDistanceFromSpaceFrontValid = axle.distanceFromSpaceFront !== null;
      // const isEmptySpaceLoadValid = axle.emptySpaceLoad !== null; // https://transeu.atlassian.net/browse/GL-3886
      const isMaxLoadValid = axle.maxLoad !== null;
      // return isDistanceFromSpaceFrontValid && isEmptySpaceLoadValid && isMaxLoadValid;
      return isDistanceFromSpaceFrontValid && isMaxLoadValid;
    });
  }

  public emitAxles(): void {
    if (this.validateAxles(this.semiAxles)) {
      const setAxles = this.validateAxles(this.trailerAxles)
        ? [...this.semiAxles, ...this.trailerAxles]
        : this.semiAxles;
      const axlesData: Axle[] = (this.setActive ? setAxles : this.semiAxles).map((axle) => ({
        ...axle,
        emptySpaceLoad: this.numericEmptySpaceLoad(axle.emptySpaceLoad),
      }));
      this.submitEmitter.emit(axlesData);
    }
  }

  private numericEmptySpaceLoad(value: number | null | undefined): number {
    return Number.isFinite(value) ? value : 0;
  }

  public delete() {
    this.form.patchValue(
      {
        numberOfAxles: 2,
        selectAxle: 1,
        distanceFromSpaceFront: null,
        emptySpaceLoad: null,
        maxLoad: null,
      },
      { emitEvent: false },
    );
    this.axles = [];
    this.semiAxles = [];
    this.trailerAxles = [];
    this.setAxles();
    this.submitEmitter.emit(undefined);
  }

  public resetToDefaultValue(): void {
    this.axles = [];
    this.semiAxles = [];
    this.trailerAxles = [];
    this.setAxles();
    this.fillFormWithAxleData(0);
    this.emitAxles();
  }

  public openInfoWindow() {
    this.isInfoWindowOpen = !this.isInfoWindowOpen;
    this.scrollDisable.emit(this.isInfoWindowOpen);
    if (this.isInfoWindowOpen) {
      window.setTimeout(() => {
        this.renderer.setStyle(
          this.infoWindowEl.nativeElement,
          'top',
          `${this.calculateInfoWindowTopPosition()}px`,
        );
        this.renderer.setStyle(
          this.infoWindowEl.nativeElement,
          'bottom',
          `${this.calculateInfoWindowBottomPosition()}px`,
        );
        this.renderer.setStyle(
          this.infoWindowEl.nativeElement,
          'left',
          `${this.calculateInfoWindowLeftPosition()}px`,
        );
        this.renderer.setStyle(
          this.infoArrowEl.nativeElement,
          'top',
          `${this.calculateInfoWindowArrowTopPosition()}px`,
        );
        this.renderer.setStyle(this.infoWindowEl.nativeElement, 'visibility', 'visible');
      });
    }
  }

  private calculateInfoWindowTopPosition(): number {
    const infoWindowRect = this.infoWindowEl.nativeElement.getBoundingClientRect();
    const infoTriggerRect = this.infoTriggerEl.nativeElement.getBoundingClientRect();
    let top = infoTriggerRect.top + infoTriggerRect.height / 2 - infoWindowRect.height / 2;
    if (top + infoWindowRect.height > window.innerHeight) {
      top = window.innerHeight - infoWindowRect.height;
    }
    if (top < 0) {
      top = 0;
    }
    return top;
  }

  private calculateInfoWindowBottomPosition(): number {
    const infoWindowRect = this.infoWindowEl.nativeElement.getBoundingClientRect();

    if (window.innerHeight - infoWindowRect.height < 0) {
      return 0;
    } else {
      return undefined;
    }
  }

  private calculateInfoWindowLeftPosition(): number {
    const infoTriggerRect = this.infoTriggerEl.nativeElement;
    return infoTriggerRect.offsetLeft + infoTriggerRect.offsetWidth + 10;
  }

  private calculateInfoWindowArrowTopPosition() {
    const infoTriggerRect = this.infoTriggerEl.nativeElement.getBoundingClientRect();
    const infoWindowRect = this.infoWindowEl.nativeElement.getBoundingClientRect();
    const ARROW_BORDER = 5;
    return infoTriggerRect.top + infoTriggerRect.height / 2 - infoWindowRect.top - ARROW_BORDER;
  }

  private generateAxleLoadsForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      // isBus: new FormControl(true),
      quantity: new UntypedFormControl(2),
      numberOfAxles: new UntypedFormControl(2),
      selectedAxle: new UntypedFormControl(1),
      distanceFromSpaceFront: new UntypedFormControl(null, [
        Validators.required,
        validateMin(-this.cabinLength),
        Validators.max(this.isSwitchSet ? this.loadingSpace.length1 : this.loadingSpace.length),
      ]),
      // emptySpaceLoad: new UntypedFormControl(null, [Validators.required]), // https://transeu.atlassian.net/browse/GL-3886
      emptySpaceLoad: new UntypedFormControl(null),
      maxLoad: new UntypedFormControl(null, [Validators.required]),
    });
  }
}

interface CustomForm extends UntypedFormGroup {
  value: Axle;
}
